import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Wrap,
  WrapItem,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaFilter,
  FaSort,
  FaEllipsisV,
} from 'react-icons/fa';
import { useTasks, useCreateTask, useUpdateTask, useDeleteTask } from '../hooks/useTasks';
import { useProjects } from '../hooks/useProjects';
import { useUsers } from '../hooks/useUsers';
import { Task, CreateTaskData, UpdateTaskData } from '../api/taskApi';
import TaskModal from './modals/TaskBuiltInFormModal';
import TaskTable from './TaskTable';
import TaskKanban from './TaskKanban';
import { PermissionGuard } from './PermissionGuard';
import { Permission } from '../utils/permissions';
import { useAuthenticatedUser } from '../contexts/AuthenticatedUserInfoContext';
import { useOrganizationEnums } from '../hooks/useOrganizationEnums';
import { formatEnumLabel } from '../utils/organizationEnums';
import { useNavigate } from 'react-router-dom';
import {
  getTaskManagementPreferences,
  updateTaskManagementPreferences,
  TaskManagementPreferences,
} from '../utils/localStorage';

const TaskManagement = () => {
  // Load preferences from localStorage
  const [preferences, setPreferences] = useState<TaskManagementPreferences>(getTaskManagementPreferences());
  
  // Hooks
  // Show all toggle state
  const [showAllTasks, setShowAllTasks] = useState(false);
  const { authenticatedUser } = useAuthenticatedUser();
  const { data: organizationEnums } = useOrganizationEnums();
  const canViewAll = !!authenticatedUser && (authenticatedUser.role === 'admin' || authenticatedUser.role === 'editor');
  // Always pass a boolean to useTasks, and only allow showAll if user can view all
  const { data: tasks, isLoading, isError, error } = useTasks(Boolean(showAllTasks && canViewAll));
  const { data: projects } = useProjects();
  const { data: users } = useUsers();
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();
  const toast = useToast();
  const navigate = useNavigate();

  // UI states
  const cardBg = useColorModeValue('white', 'gray.800');
  const {
    isOpen: isCreateModalOpen,
    onOpen: onCreateModalOpen,
    onClose: onCreateModalClose,
  } = useDisclosure();
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isViewModalOpen,
    onOpen: onViewModalOpen,
    onClose: onViewModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();

  // State - Initialize with localStorage preferences
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState(preferences.searchQuery);
  const [statusFilter, setStatusFilter] = useState(preferences.statusFilter);
  const [priorityFilter, setPriorityFilter] = useState(preferences.priorityFilter);
  const [typeFilter, setTypeFilter] = useState(preferences.typeFilter);
  const [sortBy, setSortBy] = useState<'priority' | 'status' | 'createdAt'>(
    preferences.sortBy
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(preferences.sortOrder);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(preferences.viewMode || 'table');

  // Form data
  const [taskFormData, setTaskFormData] = useState<CreateTaskData>({
    taskTitle: '',
    priority: 'medium',
    description: '',
    type: 'development',
    status: 'Not Started',
    authBy: [],
    quotedPrice: undefined,
    dateStarted: undefined,
    dueDate: undefined,
    assignedToUser: [],
  });

  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // Save preferences when they change
  const updatePreference = (updates: Partial<TaskManagementPreferences>) => {
    const newPreferences = { ...preferences, ...updates };
    setPreferences(newPreferences);
    updateTaskManagementPreferences(updates);
  };

  // Enhanced filter and sort handlers that save to localStorage
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    updatePreference({ searchQuery: value });
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    updatePreference({ statusFilter: value });
  };

  const handlePriorityFilterChange = (value: string) => {
    setPriorityFilter(value);
    updatePreference({ priorityFilter: value });
  };

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    updatePreference({ typeFilter: value });
  };

  const handleSortByChange = (value: 'priority' | 'status' | 'createdAt') => {
    setSortBy(value);
    updatePreference({ sortBy: value });
  };

  const handleSortOrderChange = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    updatePreference({ sortOrder: newOrder });
  };

  // Filtered and sorted tasks
  const filteredAndSortedTasks = useMemo(() => {
    if (!tasks) return [];

    let filtered = tasks.filter((task: Task) => {
      const matchesSearch =
        task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.type.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = !statusFilter || task.status === statusFilter;
      const matchesPriority = !priorityFilter || task.priority === priorityFilter;
      const matchesType = !typeFilter || task.type === typeFilter;

      return matchesSearch && matchesStatus && matchesPriority && matchesType;
    });

    // Sort tasks
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];

      if (sortBy === 'createdAt') {
        aValue = new Date(a.createdAt || '').getTime();
        bValue = new Date(b.createdAt || '').getTime();
      } else if (sortBy === 'priority') {
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        aValue = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
        bValue = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [
    tasks,
    searchQuery,
    statusFilter,
    priorityFilter,
    typeFilter,
    sortBy,
    sortOrder,
  ]);

  // Helper functions
  const getProjectName = (task: Task) => {
    // If task has no project
    if (!task.project) return 'No Project';

    // If project is populated (object)
    if (
      typeof task.project === 'object' &&
      task.project.projectNumber &&
      task.project.projectName
    ) {
      return `${task.project.projectNumber} - ${task.project.projectName}`;
    }

    // If project is just an ID string, find it in the projects list
    if (typeof task.project === 'string') {
      const project = projects?.find(p => p._id === task.project);
      return project ? `${project.projectNumber} - ${project.projectName}` : 'Unknown Project';
    }

    return 'No Project';
  };

  const getUserNames = (userIds: string[] | undefined) => {
    if (!userIds || userIds.length === 0) return 'Not specified';
    const userNames = userIds.map(userId => {
      const user = users?.find(u => u._id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
    });
    return userNames.join(', ');
  };

  const formatDate = (dateString: Date | string | undefined) => {
    if (!dateString) return 'Date not available';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date not available';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Date not available';
    }
  };

  const formatDateDisplay = (date: Date | string | undefined) => {
    if (!date) return null;
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return null;
      return dateObj.toLocaleDateString();
    } catch {
      return null;
    }
  };

  const resetForm = () => {
    setTaskFormData({
      taskTitle: '',
      priority: 'medium',
      description: '',
      type: 'development',
      status: 'Not Started',
      authBy: [],
      quotedPrice: undefined,
      dateStarted: undefined,
      dueDate: undefined,
      assignedToUser: [],
    });
    setSelectedProject(null);
  };

  // Event handlers
  const handleCreateTask = () => {
    resetForm();
    onCreateModalOpen();
  };

  const toDateInputValue = (date: string | Date | undefined) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setTaskFormData({
      taskTitle: task.taskTitle || '',
      priority: task.priority,
      description: task.description,
      type: task.type,
      status: task.status,
      authBy: task.authBy || [],
      quotedPrice: task.quotedPrice,
      agreedPrice: task.agreedPrice, // <-- ensure agreedPrice is set
      dateRequested: toDateInputValue(task.dateRequested), // <-- ensure dateRequested is set
      dateStarted: toDateInputValue(task.dateStarted),
      dueDate: toDateInputValue(task.dueDate),
      assignedToUser: task.assignedToUser || [],
    });

    // Set project if task has one
    if (task.project) {
      if (typeof task.project === 'object') {
        // Project is already populated
        setSelectedProject(task.project);
      } else if (typeof task.project === 'string') {
        // Project is just an ID, find it in the projects list
        const project = projects?.find(p => p._id === task.project);
        setSelectedProject(project || null);
      }
    } else {
      setSelectedProject(null);
    }

    onEditModalOpen();
  };

  const handleViewTask = (task: Task) => {
    // Navigate to task details using the proper route
    navigate(`/dashboard/task/${task._id}/details`);
  };

  const handleDeleteTask = (task: Task) => {
    setSelectedTask(task);
    onDeleteAlertOpen();
  };

  const confirmDelete = async () => {
    if (!selectedTask) return;

    try {
      await deleteTaskMutation.mutateAsync(selectedTask._id);
      toast({
        title: 'Task Deleted',
        description: `Task has been deleted successfully!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteAlertClose();
      setSelectedTask(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete task. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSubmitTask = async () => {
    // Validate required fields
    if (
      !taskFormData.taskTitle.trim() ||
      !taskFormData.priority ||
      !taskFormData.description.trim() ||
      !taskFormData.type
    ) {
      toast({
        title: 'Error',
        description: 'All required fields must be filled.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Add completedBy if status is being set to Completed
    let extraFields = {};
    if (
      selectedTask &&
      taskFormData.status === 'Completed' &&
      selectedTask.status !== 'Completed' &&
      authenticatedUser?.LoggedInUserId
    ) {
      extraFields = { completedBy: authenticatedUser.LoggedInUserId };
    }

    // Always send assignedToUser as an array
    const taskDataWithProject = {
      ...taskFormData,
      ...extraFields,
      project: selectedProject?._id || undefined,
      assignedToUser: Array.isArray(taskFormData.assignedToUser)
        ? taskFormData.assignedToUser
        : [],
      // Convert string dates back to Date objects for the backend
      dateStarted: taskFormData.dateStarted ? new Date(taskFormData.dateStarted) : undefined,
      dueDate: taskFormData.dueDate ? new Date(taskFormData.dueDate) : undefined,
    };

    try {
      if (selectedTask) {
        // Update existing task
        await updateTaskMutation.mutateAsync({
          id: selectedTask._id,
          data: taskDataWithProject as UpdateTaskData,
        });
        toast({
          title: 'Task Updated',
          description: `Task has been updated successfully!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onEditModalClose();
      } else {
        // Create new task
        await createTaskMutation.mutateAsync(taskDataWithProject);
        toast({
          title: 'Task Created',
          description: `Task has been created successfully!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onCreateModalClose();
      }
      resetForm();
      setSelectedTask(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${selectedTask ? 'update' : 'create'} task. Please try again.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleModalClose = () => {
    resetForm();
    setSelectedTask(null);
    if (selectedTask) {
      onEditModalClose();
    } else {
      onCreateModalClose();
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPriorityFilter('');
    setTypeFilter('');
    
    // Update localStorage
    updatePreference({
      searchQuery: '',
      statusFilter: '',
      priorityFilter: '',
      typeFilter: '',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'not started':
        return 'gray';
      case 'completed':
        return 'green';
      case 'in progress':
        return 'blue';
      case 'pending':
        return 'yellow';
      case 'on hold':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  if (isLoading) {
    return (
      <Box p={8} display="flex" justifyContent="center" alignItems="center" minH="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading tasks...</Text>
        </VStack>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          Error loading tasks: {error?.message || 'Unknown error occurred'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Heading size="xl" mb={2}>
              Task Management
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Manage and track all your tasks
            </Text>
          </Box>
          <HStack spacing={3}>
            {canViewAll && (
              <Button
                size="sm"
                colorScheme={showAllTasks ? 'blue' : 'gray'}
                variant={showAllTasks ? 'solid' : 'outline'}
                onClick={() => setShowAllTasks(v => !v)}
              >
                {showAllTasks ? 'Show Only My Tasks' : 'Show All Tasks'}
              </Button>
            )}
            <Button
              size="sm"
              variant={viewMode === 'table' ? 'solid' : 'outline'}
              onClick={() => {
                setViewMode('table');
                updatePreference({ viewMode: 'table' });
              }}
            >
              Table
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'grid' ? 'solid' : 'outline'}
              onClick={() => {
                setViewMode('grid');
                updatePreference({ viewMode: 'grid' });
              }}
            >
              Kanban
            </Button>
            <PermissionGuard permission={Permission.CREATE_TASKS}>
              <Button
                leftIcon={<Icon as={FaPlus} />}
                colorScheme="blue"
                size="lg"
                borderRadius="xl"
                onClick={handleCreateTask}
              >
                Create New Task
              </Button>
            </PermissionGuard>
          </HStack>
        </HStack>

        {/* Tasks Table */}
        {/* Filters and Search */}
        <Box bg={cardBg} p={4} borderRadius="xl" boxShadow="md" border="1px" borderColor="gray.200">
              <VStack spacing={4}>
                <HStack w="100%" spacing={4}>
                  <InputGroup flex={2}>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search tasks by PO number, description, or type..."
                      value={searchQuery}
                      onChange={e => handleSearchChange(e.target.value)}
                    />
                  </InputGroup>
                  <Button
                    leftIcon={<Icon as={FaFilter} />}
                    variant="outline"
                    onClick={clearFilters}
                    isDisabled={
                      !searchQuery && !statusFilter && !priorityFilter && !typeFilter
                    }
                  >
                    Clear Filters
                  </Button>
                  <Tooltip label="Reset all preferences including filters and sort order">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const defaultPrefs = {
                          searchQuery: '',
                          statusFilter: '',
                          priorityFilter: '',
                          typeFilter: '',
                          sortBy: 'createdAt' as const,
                          sortOrder: 'desc' as const,
                          viewMode: 'table' as const,
                          itemsPerPage: 25,
                        };
                        
                        // Update state
                        setSearchQuery('');
                        setStatusFilter('');
                        setPriorityFilter('');
                        setTypeFilter('');
                        setSortBy('createdAt');
                        setSortOrder('desc');
                        
                        // Update localStorage
                        updateTaskManagementPreferences(defaultPrefs);
                        setPreferences({ ...preferences, ...defaultPrefs });
                        
                        toast({ title: 'Preferences reset', status: 'info', duration: 2000 });
                      }}
                    >
                      Reset All
                    </Button>
                  </Tooltip>
                </HStack>

                <Wrap spacing={4} w="100%">
                  <WrapItem>
                    <Select
                      placeholder="All Statuses"
                      value={statusFilter}
                      onChange={e => handleStatusFilterChange(e.target.value)}
                      w="150px"
                    >
                      {organizationEnums?.taskStatuses.map((status) => (
                        <option key={status} value={status}>
                          {formatEnumLabel(status)}
                        </option>
                      ))}
                    </Select>
                  </WrapItem>

                  <WrapItem>
                    <Select
                      placeholder="All Priorities"
                      value={priorityFilter}
                      onChange={e => handlePriorityFilterChange(e.target.value)}
                      w="150px"
                    >
                      {organizationEnums?.taskPriorities.map((priority) => (
                        <option key={priority} value={priority}>
                          {formatEnumLabel(priority)}
                        </option>
                      ))}
                    </Select>
                  </WrapItem>

                  <WrapItem>
                    <Select
                      placeholder="All Types"
                      value={typeFilter}
                      onChange={e => handleTypeFilterChange(e.target.value)}
                      w="150px"
                    >
                      {organizationEnums?.taskTypes.map((type) => (
                        <option key={type} value={type}>
                          {formatEnumLabel(type)}
                        </option>
                      ))}
                            </Select>
                  </WrapItem>

                  <WrapItem>
                    <HStack>
                      <Text fontSize="sm" color="gray.600">
                        Sort by:
                      </Text>
                      <Select
                        value={sortBy}
                        onChange={e => handleSortByChange(e.target.value as any)}
                        w="130px"
                        size="sm"
                      >
                        <option value="createdAt">Date Created</option>
                        <option value="poNumber">PO Number</option>
                        <option value="priority">Priority</option>
                        <option value="status">Status</option>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Icon as={FaSort} />}
                        onClick={handleSortOrderChange}
                      >
                        {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                      </Button>
                    </HStack>
                  </WrapItem>
                </Wrap>
              </VStack>
            </Box>

            {/* Stats Row */}
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} w="full">
              <Box
                bg={cardBg}
                p={4}
                borderRadius="lg"
                boxShadow="md"
                border="1px"
                borderColor="gray.200"
                flex="1"
              >
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {filteredAndSortedTasks?.length || 0}
                </Text>
                <Text color="gray.600" fontSize="sm">
                  {filteredAndSortedTasks?.length === tasks?.length ? 'Total Tasks' : 'Filtered Tasks'}
                </Text>
              </Box>
              <Box
                bg={cardBg}
                p={4}
                borderRadius="lg"
                boxShadow="md"
                border="1px"
                borderColor="gray.200"
                flex="1"
              >
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {filteredAndSortedTasks?.filter(task => task.status.toLowerCase() === 'completed')
                    .length || 0}
                </Text>
                <Text color="gray.600" fontSize="sm">
                  Completed
                </Text>
              </Box>
              <Box
                bg={cardBg}
                p={4}
                borderRadius="lg"
                boxShadow="md"
                border="1px"
                borderColor="gray.200"
                flex="1"
              >
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {filteredAndSortedTasks?.filter(task => task.status.toLowerCase() === 'in progress')
                    .length || 0}
                </Text>
                <Text color="gray.600" fontSize="sm">
                  In Progress
                </Text>
              </Box>
              <Box
                bg={cardBg}
                p={4}
                borderRadius="lg"
                boxShadow="md"
                border="1px"
                borderColor="gray.200"
                flex="1"
              >
                <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                  {filteredAndSortedTasks?.filter(task => task.priority.toLowerCase() === 'urgent')
                    .length || 0}
                </Text>
                <Text color="gray.600" fontSize="sm">
                  Urgent Tasks
                </Text>
              </Box>
            </SimpleGrid>

            <Box
              bg={cardBg}
              borderRadius="xl"
              boxShadow="lg"
              border="1px"
              borderColor="gray.200"
              overflow="hidden"
              p={viewMode === 'grid' ? 4 : 0}
            >
              {!filteredAndSortedTasks || filteredAndSortedTasks.length === 0 ? (
                <Box p={8} textAlign="center">
                  <Text fontSize="lg" color="gray.500" mb={4}>
                    {searchQuery || statusFilter || priorityFilter || typeFilter
                      ? 'No tasks match your filters'
                      : 'No tasks found'}
                  </Text>
                  <Text color="gray.400" mb={4}>
                    {searchQuery || statusFilter || priorityFilter || typeFilter
                      ? 'Try adjusting your search criteria'
                      : 'Create your first task to get started'}
                  </Text>
                  {(searchQuery || statusFilter || priorityFilter || typeFilter) && (
                    <Button variant="outline" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </Box>
              ) : viewMode === 'grid' ? (
                <TaskKanban tasks={filteredAndSortedTasks} />
              ) : (
                <Box overflowX="auto">
                  <TaskTable
                    tasks={filteredAndSortedTasks}
                    getProjectName={getProjectName}
                    getUserNames={getUserNames}
                    getPriorityColor={getPriorityColor}
                    getStatusColor={getStatusColor}
                    handleViewTask={handleViewTask}
                    handleEditTask={handleEditTask}
                    handleDeleteTask={handleDeleteTask}
                  />
                </Box>
              )}
            </Box>

        {/* Create/Edit Task Modal */}
        <TaskModal
          isOpen={isCreateModalOpen || isEditModalOpen}
          onClose={handleModalClose}
          formData={taskFormData}
          onFormChange={data => setTaskFormData({ ...taskFormData, ...data })}
          onSubmit={handleSubmitTask}
          isLoading={createTaskMutation.isPending || updateTaskMutation.isPending}
          selectedProject={selectedProject}
          onProjectChange={setSelectedProject}
          title={selectedTask ? 'Edit Task' : 'Create New Task'}
        />

        {/* View Task Modal */}
        <Modal isOpen={isViewModalOpen} onClose={onViewModalClose} size="xl">
          <ModalOverlay />
          <ModalContent maxH="90vh" overflowY="auto">
            <ModalHeader>Task Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedTask && (
                <VStack spacing={4} align="stretch">
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <HStack justify="space-between">
                       {/* <Text fontWeight="bold" fontSize="lg">
                         {selectedTask.poNumber}
                       </Text> */}
                      <Badge colorScheme={getStatusColor(selectedTask.status)} size="lg">
                        {selectedTask.status}
                      </Badge>
                    </HStack>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Description
                    </Text>
                    <Text bg="gray.50" p={3} borderRadius="md">
                      {selectedTask.description}
                    </Text>
                  </Box>

                  <Flex wrap="wrap" gap={4}>
                    <Box flex="1" minW="200px">
                      <Text fontWeight="bold" mb={1}>
                        Type
                      </Text>
                      <Badge variant="outline" colorScheme="purple">
                        {selectedTask.type}
                      </Badge>
                    </Box>
                    <Box flex="1" minW="200px">
                      <Text fontWeight="bold" mb={1}>
                        Priority
                      </Text>
                      <Badge colorScheme={getPriorityColor(selectedTask.priority)}>
                        {selectedTask.priority.toUpperCase()}
                      </Badge>
                    </Box>
                    <Box flex="1" minW="200px">
                      <Text fontWeight="bold" mb={1}>
                        Project
                      </Text>
                      <Text color="purple.600">{getProjectName(selectedTask)}</Text>
                    </Box>
                  </Flex>

                  <Flex wrap="wrap" gap={4}>
                    {selectedTask.quotedPrice && (
                      <Box>
                        <Text fontWeight="bold" mb={1}>
                          Quoted Price
                        </Text>
                        <Text color="green.600" fontWeight="bold">
                          ${selectedTask.quotedPrice.toLocaleString()}
                        </Text>
                      </Box>
                    )}
                    {selectedTask.dateStarted && (
                      <Box>
                        <Text fontWeight="bold" mb={1}>
                          Start Date
                        </Text>
                        <Text>{formatDateDisplay(selectedTask.dateStarted)}</Text>
                      </Box>
                    )}
                    {selectedTask.dueDate && (
                      <Box>
                        <Text fontWeight="bold" mb={1}>
                          Due Date
                        </Text>
                        <Text color="orange.600" fontWeight="bold">
                          {formatDateDisplay(selectedTask.dueDate)}
                        </Text>
                      </Box>
                    )}
                    {selectedTask.assignedToUser && selectedTask.assignedToUser.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" mb={1}>
                          Assigned To
                        </Text>
                        <Text>{getUserNames(selectedTask.assignedToUser)}</Text>
                      </Box>
                    )}
                    {selectedTask.authBy && selectedTask.authBy.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" mb={1}>
                          Authorized By
                        </Text>
                        <Text>{getUserNames(selectedTask.authBy)}</Text>
                      </Box>
                    )}
                  </Flex>

                  <Box>
                    <Text fontWeight="bold" mb={1}>
                      {selectedTask.createdAt && selectedTask.updatedAt
                        ? new Date(selectedTask.updatedAt).getTime() >
                          new Date(selectedTask.createdAt).getTime() + 1000
                          ? 'Last Updated'
                          : 'Created'
                        : selectedTask.updatedAt
                          ? 'Last Updated'
                          : 'Created'}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {formatDate(selectedTask.updatedAt || selectedTask.createdAt)}
                    </Text>
                  </Box>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onViewModalClose}>
                Close
              </Button>
              <PermissionGuard permission={Permission.EDIT_TASKS}>
                <Button
                  colorScheme="blue"
                  onClick={() => {
                    onViewModalClose();
                    if (selectedTask) handleEditTask(selectedTask);
                  }}
                >
                  Edit Task
                </Button>
              </PermissionGuard>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteAlertOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteAlertClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Task
              </AlertDialogHeader>

              <AlertDialogBody>
                 Are you sure you want to delete this task? This action cannot
                 be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={confirmDelete}
                  ml={3}
                  isLoading={deleteTaskMutation.isPending}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </VStack>
    </Box>
  );
};

export default TaskManagement;
